import CryptoJS from 'crypto-js';

const SCHOOL_ACCOUNTS_KEY = 'school_accounts';
const ACTIVE_SCHOOL_ID_KEY = 'active_school_id';

const useService = () => {
  const getWorkingElements = () => {
    const serviceAcceptor = localStorage.getItem('service_acceptor');
    const serviceProvider = localStorage.getItem('service_provider');

    return { serviceAcceptor, serviceProvider };
  };
  const shadeAcceptor = (serviceAcceptor, serviceProvider) => {
    if (!serviceProvider) return '';
    return CryptoJS.AES.encrypt(JSON.stringify(serviceAcceptor), serviceProvider).toString();
  };
  const lightAcceptor = (serviceAcceptor, serviceProvider) => {
    if (!serviceAcceptor || !serviceProvider) return {};
    try {
      const decrypted = CryptoJS.AES.decrypt(serviceAcceptor, serviceProvider).toString(CryptoJS.enc.Utf8);
      if (!decrypted) return {};
      return JSON.parse(decrypted);
    } catch (err) {
      console.warn('Invalid service_acceptor payload; using safe fallback.');
      return {};
    }
  };
  const brightAcceptorHandle = () => {
    const { serviceAcceptor, serviceProvider } = getWorkingElements();
    return lightAcceptor(serviceAcceptor, serviceProvider);
  };
  const setWorkingElements = (serviceAcceptor, serviceProvider) => {
    localStorage.setItem('service_acceptor', serviceAcceptor);
    localStorage.setItem('service_provider', serviceProvider);
  };
  const handleLogout = () => {
    localStorage.removeItem('service_provider');
    localStorage.removeItem('service_acceptor');
    localStorage.removeItem('school_data');
    localStorage.removeItem('access_token');
    localStorage.removeItem('role');
    localStorage.removeItem('userSession');
    localStorage.removeItem(SCHOOL_ACCOUNTS_KEY);
    localStorage.removeItem(ACTIVE_SCHOOL_ID_KEY);
    localStorage.clear();
    window.location.replace(`${process.env.REACT_APP_CONTAINER_APP}?logout=true`);
  };

  const getSchoolAccounts = () => {
    try {
      const stored = localStorage.getItem(SCHOOL_ACCOUNTS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (err) {
      console.warn('Invalid school_accounts payload.');
    }

    const brightAcceptor = brightAcceptorHandle();
    if (Array.isArray(brightAcceptor.users) && brightAcceptor.users.length > 0) {
      return brightAcceptor.users;
    }

    if (brightAcceptor.id && brightAcceptor.access_token) {
      return [brightAcceptor];
    }

    return [];
  };

  const persistSchoolAccounts = (accounts) => {
    if (!Array.isArray(accounts) || accounts.length === 0) return;
    localStorage.setItem(SCHOOL_ACCOUNTS_KEY, JSON.stringify(accounts));
  };

  const getActiveSchoolId = () => {
    const stored = localStorage.getItem(ACTIVE_SCHOOL_ID_KEY);
    if (stored) return Number(stored);

    const brightAcceptor = brightAcceptorHandle();
    return brightAcceptor.id ?? brightAcceptor.activeSchoolId ?? null;
  };

  const getActiveSchoolAccount = () => {
    const accounts = getSchoolAccounts();
    if (accounts.length === 0) return null;

    const activeId = getActiveSchoolId();
    const matched = accounts.find((account) => String(account.id) === String(activeId));
    return matched || accounts[0];
  };

  const getSchoolLogoUrl = (schoolLogo) => {
    if (!schoolLogo) return null;
    if (schoolLogo.startsWith('http://') || schoolLogo.startsWith('https://')) return schoolLogo;
    return `${process.env.REACT_APP_API_URL}/uploads/${schoolLogo}`;
  };

  const syncSchoolAccountsFromAcceptor = (acceptor = brightAcceptorHandle()) => {
    if (!acceptor || typeof acceptor !== 'object') return [];

    const accounts =
      Array.isArray(acceptor.users) && acceptor.users.length > 0
        ? acceptor.users
        : acceptor.id && acceptor.access_token
          ? [acceptor]
          : [];

    if (accounts.length > 0) {
      persistSchoolAccounts(accounts);
      const activeId = acceptor.activeSchoolId || acceptor.id || accounts[0].id;
      localStorage.setItem(ACTIVE_SCHOOL_ID_KEY, String(activeId));
    }

    return accounts;
  };

  const fetchSchoolAccountsByPhone = async (phone) => {
    if (!phone) return [];

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/common/getLoginRole`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (data?.status && Array.isArray(data.users) && data.users.length > 0) {
        persistSchoolAccounts(data.users);
        return data.users;
      }
    } catch (err) {
      console.warn('Unable to fetch school accounts.', err);
    }

    return [];
  };

  const switchSchoolAccount = (schoolAccount) => {
    if (!schoolAccount?.access_token) return;

    const brightAcceptor = brightAcceptorHandle();
    const serviceProvider = localStorage.getItem('service_provider');
    if (!serviceProvider) return;

    const schoolAccounts = getSchoolAccounts();
    const updatedAccounts = schoolAccounts.map((account) =>
      String(account.id) === String(schoolAccount.id) ? { ...account, ...schoolAccount } : account
    );

    if (!updatedAccounts.some((account) => String(account.id) === String(schoolAccount.id))) {
      updatedAccounts.push(schoolAccount);
    }

    persistSchoolAccounts(updatedAccounts);
    localStorage.setItem(ACTIVE_SCHOOL_ID_KEY, String(schoolAccount.id));
    localStorage.removeItem('school_data');
    localStorage.setItem('access_token', schoolAccount.access_token);

    const updatedAcceptor = {
      ...brightAcceptor,
      ...schoolAccount,
      users: updatedAccounts,
      activeSchoolId: schoolAccount.id,
    };

    const newServiceAcceptor = shadeAcceptor(updatedAcceptor, serviceProvider);
    setWorkingElements(newServiceAcceptor, serviceProvider);

    window.location.href = process.env.REACT_APP_ADMIN_APP || '/';
  };
  const checkAccessTime = (response, currentPath) => {
    if (response.data) {
      if (response.data.error) {
        if (response.data.error.status === 'UN_AUTHORISED') {
          console.error('Handle Logout');
          handleLogout();
        }
      } else if (response.data.access_token) {
        
        const brightAcceptor = brightAcceptorHandle();

        const serviceProvider = localStorage.getItem('service_provider');

        let newServiceAcceptor = shadeAcceptor(
          { ...brightAcceptor, access_token: response.data.access_token },
          serviceProvider
        );

        setTimeout(() => {
          setWorkingElements(newServiceAcceptor, serviceProvider);
        }, 100);

        window.location.href = `${process.env.REACT_APP_CONTAINER_APP}?refresh=${btoa(
          JSON.stringify({ token: response.data.access_token, route: currentPath })
        )}`;
      }
    } else if (response.error) {
      console.error('Check Access Time Error');
      if (response.error.status) {
        console.error('Response Error', response);

        if (response.error.status === 'FETCH_ERROR') {
          console.error('FETCH_ERROR');
          //handleLogout();
        }
      }
    }
  };

  const fetchAccessToken = () => {
    const brightAcceptor = brightAcceptorHandle();
    const { access_token } = brightAcceptor || {};

    return access_token || localStorage.getItem('access_token') || '';
  };

  return {
    getWorkingElements,
    shadeAcceptor,
    lightAcceptor,
    brightAcceptorHandle,
    checkAccessTime,
    handleLogout,
    setWorkingElements,
    fetchAccessToken,
    getSchoolAccounts,
    getActiveSchoolId,
    getActiveSchoolAccount,
    getSchoolLogoUrl,
    syncSchoolAccountsFromAcceptor,
    fetchSchoolAccountsByPhone,
    switchSchoolAccount,
    persistSchoolAccounts,
  };
};

export default useService;
